import type { BanDich } from '../../types';
import type { viRpaLicense } from './license.vi';

export const enRpaLicense: Pick<BanDich, keyof typeof viRpaLicense> = {
  rpaLicense: {
    moTa: 'Grant, lock and renew licences per OpenCore user and manage bound devices.',
    cap: 'Grant licence',
    kpiTong: 'Users with a licence',
    kpiActive: 'Active licences',
    kpiChuaCap: 'Not granted yet',
    kpiSapHet: 'Licences expiring soon',
    kpiSapHetSub: 'In the next {{so}} days',
    tim: 'Search user / email / customer / project...',
    locTrangThai: 'Filter by licence status',
    moiTrangThai: 'All statuses',
    locThietBi: 'Filter by device',
    moiThietBi: 'All devices',
    daBind: 'Device bound',
    chuaBindLoc: 'No device bound',
    daTai: '{{so}} rows · including OpenCore users without a licence',
    nguon: 'Source: Native database + OpenCore',
    khongTaiUser: 'Could not load OpenCore users — showing only users that have a licence.',
    rong: 'No licences or OpenCore users yet',
    rongMoTa: 'Grant a licence to an OpenCore user so they can sign in to FOXAI Client and run the selected workflows.',
    rongLoc: 'No user licence matches the filters',
    cotUser: 'User',
    cotProject: 'Customer / Project',
    cotWorkflow: 'Workflows allowed',
    cotLicense: 'Licence',
    cotThietBi: 'Device',
    cotHetHan: 'Expires',
    cotTruyCap: 'Run access',
    soWorkflow: '{{so}} workflows',
    vinhVien: 'No expiry',
    chuaGanUser: 'No user linked',
    chuaBind: 'Not bound',
    thietBiKhongDoc: 'Could not read',
    xemLicense: 'View licence',
    kiemQuyen: 'Check OpenCore access',
    giaHanNut: 'Renew',
    sua: 'Edit licence',
    dongBo: 'Sync with OpenCore',
    khoa: 'Lock licence',
    moKhoa: 'Unlock licence',
    thuHoiKhongKhoa: 'This licence is revoked — it cannot be locked or unlocked.',
    resetThietBi: 'Reset device',
    thuHoi: 'Revoke licence',
    xoa: 'Delete licence',
    dongBoCanUser: 'This licence has no OpenCore user. Pick a user, then select Save.',
    daDongBo: 'Verified the OpenCore role membership.',
    daDongBoKhongThanhVien: 'Synced: the licence is not in effect, so the user is not in the licence role.',
    trangThai: {
      none: 'Not granted',
      pending: 'Not granted yet',
      active: 'Active',
      blocked: 'Locked',
      expired: 'Expired',
      revoked: 'Revoked',
    },
    loai: {
      limited: 'Limited licence',
      limitedNgan: 'Limited',
      limitedMoTa: 'View and run the selected workflows only.',
      pro: 'Pro licence',
      proNgan: 'PRO',
      proMoTa: 'Full rights: view, run, edit and update the selected workflows.',
    },
    truyCap: {
      running: 'Active',
      syncing: 'Waiting for OpenCore sync',
      syncError: 'Sync error',
      userDisabled: 'User locked',
      inactive: 'Inactive',
    },
    form: {
      capTieuDe: 'Grant licence',
      suaTieuDe: 'Edit licence',
      gioiThieuCap: 'Pick the OpenCore user, the OpenRPA project and the workflows the user may use, choose a licence type, then set the term. The internal licence code is generated for you.',
      gioiThieuSua: 'Change the user, allowed workflows, licence type, term or status. OpenCore access updates as soon as you save.',
      phanDoiTuong: 'User & project',
      phanLoai: 'Licence type',
      phanHan: 'Term & status',
      user: 'OpenCore user',
      chonUser: 'Pick an OpenCore user...',
      daCoLicense: '(has a licence)',
      project: 'OpenRPA project',
      chonProject: 'Pick a project on OpenCore...',
      projectGoiY: 'Projects come straight from OpenRPA on OpenCore. One project can license many users.',
      thieuQuyenProject: 'You need the {{quyen}} permission to read projects on OpenCore.',
      projectNhan: '{{ten}} · {{so}} workflows',
      workflow: 'Workflows the user may use',
      phamViCu: 'An older licence granted per project: the user can use every workflow of that project. To pick workflows one by one, revoke it and grant a new licence.',
      dangTaiWorkflow: 'Loading the project’s workflows...',
      projectMat: 'This project is no longer on OpenCore.',
      chonProjectTruoc: 'Pick a project to list its workflows.',
      projectChuaCoWorkflow: 'This project has no workflows on OpenCore yet.',
      daChon: '{{chon}} of {{tong}} workflows selected',
      chonHet: 'Select all',
      boChonHet: 'Clear all',
      userDaCo: 'This user already has {{so}} licences:',
      ngayHet: 'Expiry date',
      trangThai: 'Status',
      vinhVien: 'No expiry / Lifetime',
      ghiChu: 'Note',
      ghiChuGoiY: 'Internal note (optional)...',
      loiDongBoCu: 'The previous OpenCore sync failed:',
      chanGoiY: 'Active licence: the user can only view / run the selected workflows on OpenCore.',
      cap: 'Grant licence',
      luu: 'Save licence',
      loiUser: 'Pick an OpenCore user to grant the licence to.',
      loiProject: 'Pick an OpenRPA project.',
      loiWorkflow: 'Pick at least one workflow for the user.',
      loiHan: 'Pick an expiry date or tick No expiry.',
      loiHanActive: 'An active licence needs an expiry date in the future. Change the date, or pick the status Not granted yet.',
      loiTrung: 'This user already has an unrevoked licence in this project. Edit that licence instead of granting a new one.',
      daCap: 'Granted the licence.',
      daCapHongDongBo: 'Granted the licence; the OpenCore role sync failed — use Sync with OpenCore to retry.',
      daLuu: 'Saved the licence.',
      daLuuHongDongBo: 'Saved the licence; the OpenCore sync failed — use Sync with OpenCore to retry.',
    },
    giaHan: {
      tieuDe: 'Renew licence · {{ten}}',
      hienTai: 'Current expiry: {{ngay}}',
      ngayMoi: 'New expiry date',
      goiY: 'Renewing updates this licence instead of creating a new one. An expired licence becomes active again.',
      loiThieu: 'Pick a new expiry date or tick No expiry.',
      loiQuaKhu: 'The new expiry date must be in the future.',
      nut: 'Renew licence',
      xong: 'Renewed the licence.',
      xongHongDongBo: 'Renewed; the OpenCore role sync failed — use Sync with OpenCore to retry.',
    },
    quyen: {
      tieuDe: 'OpenCore access · {{ten}}',
      licenseAllowed: 'Licence in effect',
      userFound: 'User still on OpenCore',
      roleFound: 'RPA licence role still on OpenCore',
      roleMember: 'User is in the licence role',
      invokeGranted: 'Native records run access as granted',
      inSync: 'OpenCore matches Native',
      co: 'Yes',
      khong: 'No',
    },
    ngan: {
      user: 'User details',
      username: 'Username',
      email: 'Email',
      userId: 'OpenCore user ID',
      projectKhach: 'Project / Customer',
      workflow: 'Workflows allowed ({{so}})',
      khongCoWorkflow: 'The project has no workflows.',
      license: 'Licence',
      trangThai: 'Status',
      loai: 'Licence type',
      ngayCap: 'Issued',
      ngayHet: 'Expires',
      ghiChu: 'Note',
      maNoiBo: 'Internal licence code',
      hienMa: 'Show code',
      anMa: 'Hide code',
      thietBi: 'Device',
      thietBiKhongDoc: 'Could not read the device list. Select Refresh to try again.',
      tenMay: 'Machine name',
      hwid: 'HWID',
      ngayBind: 'Bound on',
      lanCuoi: 'Last active',
      heDieuHanh: 'OS / Client',
      lichSuMay: 'Device history ({{so}})',
      daReset: 'reset at {{luc}} by {{boi}}',
    },
    xacNhan: {
      khoaTieuDe: 'Lock the licence of {{user}}?',
      khoaThan: '{{user}} · {{project}}: the user can no longer sign in to FOXAI Client and is removed from the licence role on OpenCore. You can unlock it at any time.',
      moKhoaTieuDe: 'Unlock the licence of {{user}}?',
      moKhoaThan: '{{user}} · {{project}}: the user can sign in to Client again if the licence has not expired, still on the bound device only. An expired licence must be renewed first.',
      thuHoiTieuDe: 'Revoke the licence of {{user}}?',
      thuHoiThan: '{{user}} · {{project}}: the user can no longer sign in to Client and is removed from the licence role (unless they have an active licence in another project). The record is kept as history and cannot be reopened, but you can grant the user a new licence in this project.',
      resetTieuDe: 'Reset the device of {{user}}?',
      resetThan: 'Machine {{may}} is unbound from the licence. The next Client sign-in binds a new machine; the old offline lease stops working.',
      xoaTieuDe: 'Delete the licence of {{user}} ({{project}})?',
      xoaThan: 'The licence and its device links are deleted permanently and cannot be restored; the user loses the licence role. The OpenCore user, project and workflows stay. To pause instead, lock the licence.',
      daKhoa: 'Locked the licence.',
      daMoKhoa: 'Unlocked the licence.',
      doiHongDongBo: 'Changed the licence status, but the OpenCore role sync failed — use Sync with OpenCore to retry.',
      daThuHoi: 'Revoked the licence.',
      daReset: 'Reset the device. The user binds a new machine at the next sign-in.',
      daXoa: 'Deleted the licence.',
    },
  },
};
